import {
  AlertEvents,
  AlertsInput,
  AlertsOutput,
  ListLookupResponse,
  DataLakeQueriesOutput,
  ListSavedQueriesInput,
  ListSavedQueriesResponse,
  Query,
  QueryAlertsArgs,
  QueryListSavedQueriesArgs,
  ResolversParentTypes,
  Permission,
  Severity,
  HoldingTankRawDataResponse,
  QueryHoldingTankRawDataArgs,
  HoldingTankRawDataInput,
  ListLookupsInput,
  RelatedAlertsOutput,
  ReplayedAlertsInput,
  ReplayedAlertsOutput,
  QueryDataLakeQueriesArgs,
  DataLakeQueriesInput,
  QueryListLookupsArgs,
  QueryReplayedAlertsArgs,
  Mutation,
  QueryHoldingTankTestSchemasResultArgs,
  HoldingTankTestSchemasResultInput,
  HoldingTankTestSchemasResultsResponse,
  QueryViewBucketDataArgs,
  ViewBucketDataInput,
  QueryJiraOrgMetadataArgs,
  DashboardsOutput,
  DashboardsInput,
  QueryDashboardsArgs,
  DataLakePropertyDefinitionsOutput,
  DataLakePropertyDefinitionsInput,
  QueryDataLakePropertyDefinitionsArgs,
  AIInferenceStreamsMetadataList,
  AISkillsInput,
  AISkillsOutput,
  QueryAiSkillsArgs,
  AIInferenceStreamTextSearchResultList,
  McpServersOutput,
  ListMcpServersInput,
  QueryMcpServersArgs,
  QueryMcpServerArgs,
  AIConversationsOutput,
  AIConversationsInput,
  ListScheduledPromptsResponse,
  ListScheduledPromptsInput,
} from 'Generated/schema';
import storage from 'Helpers/storage';
import { ANALYTICS_CONSENT_STORAGE_KEY, PANTHER_TIMEZONE_STORAGE_KEY } from 'Source/constants';
import {
  FieldPolicy,
  FieldReadFunction,
  Reference,
  TypePolicies as ApolloTypePolicies,
} from '@apollo/client';
import { expandPermissions } from 'Helpers/utils';
import { endlessPaginationDefaultMerge, sortSeverities, mergeNotifications } from './utils';

interface FixedLengthArray<T, L extends number> extends Array<T> {
  0: T;
  length: L;
}

type FieldValues<T> =
  | FieldPolicy<T, T, T | Reference | undefined>
  | FieldReadFunction<T, T | Reference | undefined>;

type TypePolicy<T> = {
  keyFields?: keyof T | (keyof T)[] | false;
  merge?: boolean | ((existing: T | undefined, incoming: T) => T);
  fields?: Partial<{
    [P in keyof T]: FieldValues<T[P]>;
  }>;
};

export type TypePolicies = Partial<{
  [T in keyof ResolversParentTypes]: TypePolicy<ResolversParentTypes[T]>;
}> & {
  Query: TypePolicy<Query>;
  Mutation?: TypePolicy<Mutation>;
};

const typePolicies: TypePolicies = {
  Query: {
    fields: {
      detectionFilter: {
        keyArgs: ['detectionId'],
      },

      destination(existing, { args, toReference }) {
        return (
          existing || toReference(args.id ? { __typename: 'Destination', outputId: args.id } : {})
        );
      },
      jiraOrgMetadata: {
        keyArgs: (args: QueryJiraOrgMetadataArgs) => {
          if (args.input?.outputId) {
            return args.input.outputId;
          }
          if (args.input?.config) {
            // Create a cache key based on these fields
            const { apiKey, issueType, orgDomain, projectKey, userName } = args.input.config;
            // Convert the config object to a stable string representation
            return JSON.stringify({ apiKey, issueType, orgDomain, projectKey, userName });
          }
          return null;
        },
      },
      getAvailableLogProvider(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'AvailableLogProvider', id: args.id });
      },
      cloudAccount(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'CloudAccount', id: args.id });
      },
      getDataModel(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'DataModel', id: args.id });
      },
      getAnalysisPack(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'AnalysisPack', id: args.id });
      },
      dashboards: {
        keyArgs: (): [keyof QueryDashboardsArgs, FixedLengthArray<keyof DashboardsInput, 5>] => [
          'input',
          ['createdBy', 'includeDeleted', 'nameIncludes', 'pageSize', 'sortBy'],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<DashboardsOutput>(
          'dashboards',
          variables => variables.input?.cursor,
        ),
      },
      mcpServers: {
        keyArgs: (): [
          keyof QueryMcpServersArgs,
          FixedLengthArray<keyof ListMcpServersInput, 1>,
        ] => ['input', ['pageSize']],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<McpServersOutput>(
          'edges',
          variables => variables.input?.cursor,
        ),
      },
      mcpServer(existing, { args, toReference }) {
        return (
          existing || toReference({ __typename: 'McpServer', id: (args as QueryMcpServerArgs).id })
        );
      },
      dataLakeDatabase(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'DataLakeDatabase', name: args.name });
      },
      dataLakeDatabaseTable(existing, { args, toReference }) {
        return (
          existing ||
          toReference({
            __typename: 'DataLakeDatabaseTable',
            name: args.input.tableName,
            databaseName: args.input.databaseName,
          })
        );
      },
      roleById(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'Role', id: args.id });
      },
      aiInferenceStream(existing, { args, toReference }) {
        return (
          existing || toReference({ __typename: 'AIInferenceStream', streamId: args.streamId })
        );
      },
      aiSkills: {
        // pageSize is included to prevent last-write-wins races between the
        // dependency picker (ListAISkillNames, pageSize=200) and the main list
        // (ListAISkills, default pageSize), which can otherwise share a cache
        // entry when their other filter args align.
        keyArgs: (): [keyof QueryAiSkillsArgs, FixedLengthArray<keyof AISkillsInput, 5>] => [
          'input',
          ['namespace', 'nameContains', 'includeDisabled', 'includeDeleted', 'pageSize'],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<AISkillsOutput>(
          'edges',
          variables => variables.input?.cursor,
        ),
      },
      aiConversations: {
        // Scope the cache key to filter args only (not cursor).
        // pageSize is included to prevent last-write-wins races between the
        // button lookup (pageSize=1) and the thread list (pageSize=25), which
        // share the same alias+kind but serve different UI purposes.
        //
        // `ids` is intentionally excluded: the bookmarks tab passes a dynamic ID
        // list that changes on every toggle, which would otherwise accumulate one
        // cache entry per ID-set. That call site uses fetchPolicy: 'network-only'
        // so it never reads the (potentially stale) slot — only the network result
        // is displayed, and it overwrites the slot via the merge below.
        //
        // Key on every other filter via a denylist (omit cursor + ids) rather than
        // an allowlist, so a new filter participates without editing here. Sorting
        // the keys keeps the slot stable regardless of input key order; callers must
        // still send `undefined` for defaults and order `categories` stably (see
        // useConversationsTab).
        keyArgs: (args: { input?: AIConversationsInput | null } | null) => {
          const input = args?.input ?? {};
          const filters = Object.fromEntries(
            Object.entries(input)
              .filter(([key]) => key !== 'cursor' && key !== 'ids')
              .sort(([a], [b]) => a.localeCompare(b)),
          );
          return JSON.stringify(filters);
        },
        merge: endlessPaginationDefaultMerge<AIConversationsOutput>(
          'edges',
          variables => variables?.input?.cursor,
        ),
      },
      listScheduledPrompts: {
        keyArgs: (args: { input?: ListScheduledPromptsInput | null } | null) => {
          return JSON.stringify({
            createdBy: args?.input?.createdBy,
            onlyEnabled: args?.input?.onlyEnabled,
            showDeleted: args?.input?.showDeleted,
          });
        },
        merge: endlessPaginationDefaultMerge<ListScheduledPromptsResponse>(
          'edges',
          variables => variables?.input?.cursor,
        ),
      },
      aiInferenceStreamsMetadata: {
        keyArgs: ['input', ['alias']],
        merge: endlessPaginationDefaultMerge<AIInferenceStreamsMetadataList>(
          'edges',
          variables => variables?.input?.cursor,
        ),
      },
      aiInferenceStreamTextSearch: {
        keyArgs: (
          args: {
            input?: {
              query?: string;
              alias?: string;
              isConversation?: boolean;
              searchMine?: boolean;
              pageSize?: number;
              kind?: string;
            } | null;
          } | null,
        ) => {
          return JSON.stringify({
            query: args?.input?.query,
            alias: args?.input?.alias,
            isConversation: args?.input?.isConversation,
            searchMine: args?.input?.searchMine,
            pageSize: args?.input?.pageSize,
            kind: args?.input?.kind,
          });
        },
        merge: endlessPaginationDefaultMerge<AIInferenceStreamTextSearchResultList>(
          'edges',
          variables => variables?.input?.cursor,
        ),
      },
      getSavedQuery(existing, { args, toReference }) {
        return existing || toReference({ __typename: 'SavedQuery', id: args.id });
      },
      metrics(existing, { args, toReference }) {
        return (
          existing ||
          toReference({
            __typename: 'MetricsOutput',
            intervalInMinutes: args.input.intervalInMinutes,
            fromDate: args.input.fromDate,
            toDate: args.input.toDate,
          })
        );
      },
      alerts: {
        keyArgs: (): [keyof QueryAlertsArgs, FixedLengthArray<keyof AlertsInput, 17>] => [
          'input',
          [
            'assigneeIds',
            'detectionId',
            'severities',
            'logTypes',
            'resourceTypes',
            'logSources',
            'types',
            'type',
            'nameContains',
            'createdAtBefore',
            'createdAtAfter',
            'statuses',
            'eventCountMin',
            'eventCountMax',
            'sortBy',
            'sortDir',
            'subtypes',
          ],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<AlertsOutput>(
          'edges',
          variables => variables.input?.cursor,
        ),
      },
      listSavedQueries: {
        keyArgs: (): [
          keyof QueryListSavedQueriesArgs,
          FixedLengthArray<keyof ListSavedQueriesInput, 10>,
        ] => [
          'input',
          [
            'contains',
            'createdBefore',
            'createdAfter',
            'showDeleted',
            'userId',
            'sortDir',
            'isScheduled',
            'isActive',
            'tags',
            'queryKind',
          ],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<ListSavedQueriesResponse>('savedQueries'),
      },
      dataLakeQueries: {
        keyArgs: (): [
          keyof QueryDataLakeQueriesArgs,
          FixedLengthArray<keyof DataLakeQueriesInput, 7>,
        ] => [
          'input',
          [
            'kind',
            'contains',
            'status',
            'issuedBy',
            'startedAtBefore',
            'startedAtAfter',
            'sortDir',
          ],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<DataLakeQueriesOutput>(
          'edges',
          variables => variables.input?.cursor,
        ),
      },
      dataLakePropertyDefinitions: {
        keyArgs: (): [
          keyof QueryDataLakePropertyDefinitionsArgs,
          FixedLengthArray<keyof DataLakePropertyDefinitionsInput, 2>,
        ] => ['input', ['databases', 'tables']],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<DataLakePropertyDefinitionsOutput>(
          'edges',
          variables => variables.input?.cursor,
        ),
      },
      notifications: {
        read(existing) {
          return existing;
        },
        merge: mergeNotifications,
      },
      listLookups: {
        keyArgs: (): [keyof QueryListLookupsArgs, FixedLengthArray<keyof ListLookupsInput, 2>] => [
          'input',
          ['managed', 'importMethods'],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<ListLookupResponse>('lookups'),
      },
      holdingTankRawData: {
        keyArgs: (): [
          keyof QueryHoldingTankRawDataArgs,
          FixedLengthArray<keyof HoldingTankRawDataInput, 3>,
        ] => ['input', ['sourceId', 'streamType', 'filters']],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<HoldingTankRawDataResponse>(
          'data',
          variables => variables.input.paginationToken,
        ),
      },
      holdingTankTestSchemasResult: {
        keyArgs: (): [
          keyof QueryHoldingTankTestSchemasResultArgs,
          FixedLengthArray<keyof HoldingTankTestSchemasResultInput, 3>,
        ] => ['input', ['taskId', 'logType', 'prefix']],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<HoldingTankTestSchemasResultsResponse>(
          'results',
          variables => variables.input.paginationToken,
        ),
      },
      viewBucketData: {
        keyArgs: (): [
          keyof QueryViewBucketDataArgs,
          FixedLengthArray<keyof ViewBucketDataInput, 3>,
        ] => ['input', ['sourceId', 'filters', 'streamType']],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<HoldingTankRawDataResponse>(
          'data',
          variables => variables.input.paginationToken,
        ),
      },
      replayedAlerts: {
        keyArgs: (): [
          keyof QueryReplayedAlertsArgs,
          FixedLengthArray<keyof ReplayedAlertsInput, 10>,
        ] => [
          'input',
          [
            'types',
            'sortDir',
            'replayId',
            'logTypes',
            'severities',
            'nameContains',
            'eventCountMin',
            'eventCountMax',
            'createdAtAfter',
            'createdAtBefore',
          ],
        ],
        read(existing) {
          return existing;
        },
        merge: endlessPaginationDefaultMerge<ReplayedAlertsOutput>(
          'alerts',
          variables => variables.input?.cursor,
        ),
      },
    },
  },
  Destination: {
    keyFields: ['outputId'],
    fields: {
      defaultForSeverity: {
        read(existing: Severity[]) {
          return existing ? sortSeverities(existing) : existing;
        },
      },
    },
  },
  DetectionFilter: {
    keyFields: ['detectionId'],
  },
  AIConversation: {
    fields: {
      streams: {
        keyArgs: (args: { input?: { pageSize?: number; rootOnly?: boolean } } | null) => {
          return JSON.stringify({
            pageSize: args?.input?.pageSize,
            rootOnly: args?.input?.rootOnly,
          });
        },
        merge: endlessPaginationDefaultMerge<AIInferenceStreamsMetadataList>(
          'edges',
          variables => variables?.streamsInput?.cursor,
        ),
      },
    },
  },
  AIInferenceStream: {
    keyFields: ['streamId'],
    fields: {
      // Make optional fields merge properly - use incoming value or keep existing
      userPrompt: {
        merge(existing, incoming) {
          return incoming !== undefined ? incoming : existing;
        },
      },
      usage: {
        merge(existing, incoming) {
          return incoming !== undefined ? incoming : existing;
        },
      },
    },
  },
  Alert: {
    keyFields: ['id'],
    fields: {
      origin: {
        // @ts-ignore
        // Because we have a union, TS complains that it can't merge type A and type B. We're always merging the same
        // type though since an Alert can't have 2 different origin types, so we're good
        merge(existing, incoming) {
          return existing ? { ...existing, ...incoming } : incoming;
        },
      },
      activityHistory: {
        keyArgs: false,
      },
      status: {
        read(existing) {
          return existing;
        },
      },
    },
  },
  AlertOriginRule: {
    keyFields: false,
    fields: {
      events: {
        keyArgs: false,
        merge: endlessPaginationDefaultMerge<AlertEvents>(
          'items',
          variables => variables.eventsInput?.exclusiveStartKey,
        ),
      },
    },
  },
  AlertOriginReplayedRule: {
    keyFields: false,
    fields: {
      events: {
        keyArgs: false,
        merge: endlessPaginationDefaultMerge<AlertEvents>(
          'items',
          variables => variables.eventsInput?.exclusiveStartKey,
        ),
      },
    },
  },
  AnalysisPack: {
    keyFields: ['id'],
    fields: {
      enabled: {
        read(existing) {
          return existing;
        },
      },
    },
  },
  AvailableLogProvider: {
    keyFields: ['id'],
    fields: {
      details: {
        merge: false,
      },
      availableParsers: {
        merge: false,
      },
    },
  },
  AlertOriginSystemError: {
    keyFields: false,
    fields: {
      events: {
        keyArgs: false,
        merge: endlessPaginationDefaultMerge<AlertEvents>(
          'items',
          variables => variables.eventsInput?.exclusiveStartKey,
        ),
      },
      alerts: {
        keyArgs: false,
        merge: endlessPaginationDefaultMerge<RelatedAlertsOutput>(
          'edges',
          variables => variables.input?.cursor,
        ),
      },
    },
  },
  ComplianceItem: {
    keyFields: ['policyId', 'resourceId'],
  },
  SavedQuery: {
    fields: {
      defaultDatabase: {
        read: defaultDatabase => {
          // NOTE: When defaultDatabase is '' we consider it null
          if (defaultDatabase === '') {
            return null;
          }
          return defaultDatabase;
        },
      },
    },
  },
  Schema: {
    keyFields: ['name'],
  },
  // CatalogEnrichment has no `id` field — the catalog `name` (LookupName) is the stable identifier.
  // Without this policy Apollo would log a "Missing field 'id'" warning on every list / install
  // response and could not auto-merge the install mutation's payload onto the browse list row.
  CatalogEnrichment: {
    keyFields: ['name'],
  },
  S3LogIntegration: {
    keyFields: ['integrationId'],
  },
  CloudWatchLogIntegration: {
    keyFields: ['integrationId'],
  },
  EventBridgeIntegration: {
    keyFields: ['integrationId'],
  },
  AmazonSecurityLakeLogIntegration: {
    keyFields: ['integrationId'],
  },
  LogPullingIntegration: {
    keyFields: ['integrationId'],
  },
  DataLakeDatabase: {
    keyFields: ['name'],
  },
  DataLakeDatabaseTable: {
    keyFields: ['name', 'databaseName'],
  },
  EnrichmentIntegration: {
    keyFields: ['integrationId'],
  },
  DataLakeQuery: {
    keyFields: ['id'],
    fields: {
      results: {
        keyArgs: (args, { variables }) => {
          return JSON.stringify({
            id: variables.id,
            component: variables.component ?? null,
            pageSize: args?.input?.pageSize,
          });
        },
      },
    },
  },
  SqsLogSourceIntegration: {
    keyFields: ['integrationId'],
  },
  HttpLogSourceIntegration: {
    keyFields: ['integrationId'],
  },
  OTLPLogSourceIntegration: {
    keyFields: ['integrationId'],
  },
  GcsLogSourceIntegration: {
    keyFields: ['integrationId'],
  },
  GcpPubSubLogSourceIntegration: {
    keyFields: ['integrationId'],
  },
  LogForwarderLogSourceIntegration: {
    keyFields: ['integrationId'],
  },
  AzureEventHubSourceIntegration: {
    keyFields: ['integrationId'],
  },
  SourceMetricsResponse: {
    keyFields: [],
  },
  MetricsOutput: {
    keyFields: ['__typename', 'intervalInMinutes', 'fromDate', 'toDate'],
  },
  IngestionMonitoringSummary: {
    keyFields: ['__typename'],
  },
  GeneralSettingsConfig: {
    keyFields: ['__typename'],
    fields: {
      analyticsConsent: {
        merge(_, incoming) {
          storage.local.write(ANALYTICS_CONSENT_STORAGE_KEY, incoming);
          return incoming;
        },
      },
    },
  },
  SamlConfig: {
    keyFields: ['__typename'],
  },
  UniversalSettingsConfig: {
    keyFields: ['__typename'],
    fields: {
      timezone: {
        merge(_, incoming) {
          storage.local.write(PANTHER_TIMEZONE_STORAGE_KEY, incoming);
          return incoming;
        },
      },
    },
  },
  // Singleton per user — no `id` on the schema. Without this,
  // `getUserPreferences` and `putUserPreferences` end up in separate
  // cache entries and the mutation's payload never refreshes
  // `cache-first` reads (toggles revert on remount).
  UserPreferencesOutput: {
    keyFields: ['__typename'],
  },
  Role: {
    fields: {
      permissions: {
        read(existing: Permission[]) {
          return existing ? expandPermissions(existing) : existing;
        },
      },
    },
  },
  APIToken: {
    fields: {
      permissions: {
        read(existing: Permission[]) {
          return existing ? expandPermissions(existing) : existing;
        },
      },
    },
  },
  DetectionTestDefinition: {
    keyFields: false,
  },
  QueryUserConfig: {
    keyFields: ['queryId'],
    fields: {
      chartProperties: {
        merge: false,
      },
      tableProperties: {
        merge: false,
      },
    },
  },
  AIToolInvocation: {
    keyFields: ['id'],
  },
  McpUserConnection: {
    // McpUserConnection has no `id` — it's embedded on McpServer.
    // Without merge: true, Apollo replaces the entire object on each query,
    // causing the list query's fields (externalUserEmail, connectedAt) to be
    // wiped when the detail query (which fetches tools instead) writes to cache.
    keyFields: false,
    merge: true,
  },
  ScheduledPrompt: {
    keyFields: ['id'],
  },
};

export default typePolicies as unknown as ApolloTypePolicies;
